import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { loadCatalog } from "src/business/catalog/apiCatalog";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { loadWorkflow } from "src/business/workflow/apiWorkflow";
import { getWorkflowDisplayName } from "src/business/workflow/getWorkflowDisplayName";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import { findFirstUserStep } from "src/business/workflow/workflowSteps";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import { ZUserWorkflowStep } from "src/business/workflow/ZWorkflowStep";
import { RemoteData } from "src/common/RemoteData";
import { CDSFormStore } from "src/pages/catalogs/CatalogRecordForm/CDSFormStore";
import {
  createWorkflowRecordFormLoader,
  WorkflowRecordFormContext,
} from "src/pages/catalogs/CatalogRecordForm/createWorkflowRecordFormLoader";
import { PageUrl } from "src/routes/PageUrl";

export type WfNewRecordData = {
  catalog: ZCatalog;
  workflow: ZWorkflow;
};

export const createWfNewRecordStore = () =>
  makeAutoObservable({
    async init(catalogId: string, workflowId: string) {
      this.setCatalogId(catalogId);
      this.setWorkflowId(workflowId);
      try {
        this.setData({ status: "wait" });
        const [catalog, workflow] = await Promise.all([
          loadCatalog(catalogId),
          loadWorkflow(workflowId),
        ]);
        if (workflow.catalog?.id !== catalogId) {
          throw Error("Workflow не соответствует выбранному каталогу");
        }
        const firstStep = findFirstUserStep(workflow.steps ?? []);
        if (!firstStep) {
          // Реестр ошибок, п. 4.10
          throw Error(
            `Нельзя создать рабочую версию в рабочем потоке «${getWorkflowDisplayName(workflow)}»: ни у одного пользовательского шага не указан предшественник Initial. Администратору необходимо указать Initial у пользовательского шага.`,
          );
        }
        this.setData({ status: "ready", result: { catalog, workflow } });
        this.setCDSFormStore(
          new CDSFormStore(createWorkflowRecordFormLoader(catalog), {
            step: firstStep,
          }),
        );
      } catch (error) {
        this.setData({ status: "error", error });
      }
    },
    catalogId: "",
    setCatalogId(id: string) {
      this.catalogId = id;
    },
    workflowId: "",
    setWorkflowId(id: string) {
      this.workflowId = id;
    },
    data: { status: "none" } as RemoteData<WfNewRecordData>,
    setData(newData: RemoteData<WfNewRecordData>) {
      this.data = newData;
    },
    cdsFormStore: null as CDSFormStore<WorkflowRecordFormContext> | null,
    setCDSFormStore(store: CDSFormStore<WorkflowRecordFormContext>) {
      this.cdsFormStore = store;
    },
    get breadcrumbs(): BreadcrumbItem[] {
      const res: BreadcrumbItem[] = [
        { title: "Рабочие потоки и версии", page: PageUrl.workflowStreams },
      ];
      if (this.data.status === "ready") {
        res.push({
          title: getWorkflowDisplayName(this.data.result.workflow),
          page: PageUrl.workflowCards,
          params: { id: this.data.result.workflow.id },
        });
      }
      res.push({ title: "Новая рабочая версия" });
      return res;
    },
    get firstStep(): ZUserWorkflowStep | undefined {
      const { data } = this;
      if (data.status !== "ready") return undefined;
      return findFirstUserStep(data.result.workflow.steps ?? []);
    },
    get currentStepName(): string | undefined {
      return getWorkflowStepDisplayName(this.firstStep) || undefined;
    },
    get currentStepDescription(): string | null | undefined {
      return this.firstStep?.description;
    },
  });

export type WfNewRecordStore = ReturnType<typeof createWfNewRecordStore>;
